import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import { ACTIVATION_SUB_STEP, type ActivationSubStepId } from '@config/spaceActivation';
import type { ActivationDrawerProps } from './types';

type LazyDrawer = LazyExoticComponent<ComponentType<ActivationDrawerProps>>;

/**
 * Sub-step → drawer map. Every ActivationSubStepId must appear, so adding a new one is
 * a type error until someone decides what it renders. `null` means "deliberately no
 * drawer": these three are intercepted before openSubStepDrawer is ever called —
 * CommunityDetail.tsx:831 sends watch_video to openVideo, :837 navigates complete_setup
 * to SetupSpace, :874 navigates invite_members to AdminInviteScreen. Lazy so each
 * illustration only loads when its drawer opens.
 */
export const SUB_STEP_DRAWERS: Record<ActivationSubStepId, LazyDrawer | null> = {
  [ACTIVATION_SUB_STEP.SETUP_WATCH_VIDEO]: null,
  [ACTIVATION_SUB_STEP.SETUP_COMPLETE_SETUP]: null,
  [ACTIVATION_SUB_STEP.ANNOUNCE_INVITE_MEMBERS]: null,
  [ACTIVATION_SUB_STEP.SETUP_SEED_POSTS]: lazy(() =>
    import('./SetupSeedPostsDrawer').then(m => ({ default: m.SetupSeedPostsDrawer })),
  ),
  [ACTIVATION_SUB_STEP.ANNOUNCE_SHARE_LINK]: lazy(() =>
    import('./AnnounceShareLinkDrawer').then(m => ({ default: m.AnnounceShareLinkDrawer })),
  ),
  [ACTIVATION_SUB_STEP.ANNOUNCE_ANNOUNCE]: lazy(() =>
    import('./AnnounceAnnounceDrawer').then(m => ({ default: m.AnnounceAnnounceDrawer })),
  ),
  [ACTIVATION_SUB_STEP.LAUNCH_ADD_MEETBALL_APP]: lazy(() =>
    import('./LaunchAddMeetballAppDrawer').then(m => ({ default: m.LaunchAddMeetballAppDrawer })),
  ),
  [ACTIVATION_SUB_STEP.LAUNCH_ACTIVATION_KIT]: lazy(() =>
    import('./LaunchActivationKitDrawer').then(m => ({ default: m.LaunchActivationKitDrawer })),
  ),
  [ACTIVATION_SUB_STEP.LAUNCH_LIST_EVENT]: lazy(() =>
    import('./LaunchListEventDrawer').then(m => ({ default: m.LaunchListEventDrawer })),
  ),
};
